import moment from 'moment-timezone'
import {
  DATE_FORMAT,
  DATE_FORMAT_SHORT,
  DATETIME_FORMAT,
  DATETIME_FORMAT_SHORT,
  HOUR_DATETIME_FORMAT,
  ISO_DATE_FORMAT,
  ISO_DATETIME_FORMAT,
  ISO_DATETIME_FORMAT_MILISECONDS,
  MONTH_NUMBER_FORMAT,
  MONTH_STRING_SHORT_FORMAT,
  NATURAL_DATE_FORMAT_SHORT,
  NATURAL_DATE_SHORT_WITH_HOUR,
  NATURAL_DATETIME_FORMAT,
  NATURAL_NAME_DATETIME_FORMAT,
  NATURAL_SHORT_WITH_HOUR,
  UNAWARE_DATETIME_FORMAT,
} from 'frontend/constants/dateConstants'
import { store } from 'frontend/store/store'

export function getLastDaysDate (dayOffset = -7) {
  return {
    from: getFactoryUTCDate(undefined,{ dayOffset: dayOffset, startOf: 'day' }),
    to: getFactoryUTCDate(undefined, { endOf: 'day' }),
  }
}

export function getLastWeeksDate (weekOffset = -3) {
  return {
    from: getFactoryUTCDate(undefined,{ weekOffset: weekOffset, startOf: 'isoWeek' }),
    to: getFactoryUTCDate(undefined, { endOf: 'isoWeek' }),
  }
}

export function getLastMonthsDates (monthOffset = -3) {
  return {
    from: getFactoryUTCDate(undefined,{ monthOffset: monthOffset, startOf: 'month' }),
    to: getFactoryUTCDate(undefined, { endOf: 'month' }),
  }
}

export function getFirstDayOfYearDate (year = getYear()) {
  return year + '-01-01'
}

export function getLastDayYearDate (year = getYear()) {
  return year + '-12-31'
}

export function getUTCNow () {
  return moment.tz(moment(), moment.tz.guess()).utc().format(ISO_DATETIME_FORMAT)
}

export function getUTCDateBefore (unit = 'days', value = 1) {
  return moment.tz(moment().subtract(value, unit), moment.tz.guess()).utc().format(ISO_DATETIME_FORMAT)
}

export function getTodayUTCBegin () {
  return getFactoryUTCDate(undefined, { startOf: 'd', format: ISO_DATETIME_FORMAT })
}

export function getTodayUTCEnd () {
  return getFactoryUTCDate(undefined, { dayOffset: 1, startOf: 'd' })
}

export function getYesterdayUTCBegin () {
  return getFactoryUTCDate(undefined, { dayOffset: -1, startOf: 'd', format: ISO_DATETIME_FORMAT })
}

export function getYesterdayUTCEnd () {
  return getFactoryUTCDate(undefined, { dayOffset: -1, endOf: 'd', format: ISO_DATETIME_FORMAT })
}

export function getPreviousDay (date) {
  return moment(date).subtract(1, 'd')
}

export function getNextDay (date) {
  return moment(date).add(1, 'd')
}

export function getUTCDatetimeFromDate (date) {
  if (!date) { return null }
  return getDateObject(date)
}

export function getUTCYesterdayFirstShift (hour) {
  const datetime = moment().subtract(1, 'd')
  return datetime.utc().hour(hour).format(ISO_DATETIME_FORMAT)
}

export function getUTCTodayFirstShift (hour) {
  const datetime = moment()
  return datetime.utc().hour(hour).format(ISO_DATETIME_FORMAT)
}

export function getMonthName (month) {
  const monthFormatString = isNaN(month) ? MONTH_STRING_SHORT_FORMAT : MONTH_NUMBER_FORMAT
  return moment(month, monthFormatString).format(MONTH_STRING_SHORT_FORMAT)
}

export function getCurrentDay () {
  return moment().date()
}

export function getMonthDays () {
  return moment().tz(store.getters.getFactoryTimezone).daysInMonth()
}

export function getDateObject (date = undefined) {
  return getFactoryUTCDate(date)
}

export function getWeekNumber (date, applyTimezone = true) {
  const finalDate =  applyTimezone ? moment(date).tz(store.getters.getFactoryTimezone) : moment(date)
  return finalDate.isoWeek()
}

export function getDayOfTheMonth (date) {
  return moment(date).tz(store.getters.getFactoryTimezone).date()
}

export function getDayOfTheWeek (date) {
  return moment(date).tz(store.getters.getFactoryTimezone).day()
}

export function getYear (date) {
  return moment(date).tz(store.getters.getFactoryTimezone).year()
}

export function getMonth (date) {
  return moment(date).tz(store.getters.getFactoryTimezone).month()
}

export function getCustomFormattedDetailsDate (date, format) {
  return moment(date).format(format)
}

export function getWeeksOfYear (year = null) {
  const weeksOnYear = getWeekNumber(new Date(year, 11, 31))
  return weeksOnYear > 1 ? weeksOnYear : 52
}

export function formatTimeInSec (sec) {
  const sec_num = parseInt(sec, 10)
  let hours   = Math.floor(sec_num / 3600)
  let minutes = Math.floor((sec_num - (hours * 3600)) / 60)
  let seconds = sec_num - (hours * 3600) - (minutes * 60)

  if (hours   < 10) { hours   = "0"+hours }
  if (minutes < 10) { minutes = "0"+minutes }
  if (seconds < 10) { seconds = "0"+seconds }
  return hours+':'+minutes+':'+ seconds
}

// updated and new functions

function dateDiff (firstDate, secondDate, precision = 'd') {
  const m1 = moment(firstDate).utc()
  const m2 = moment(secondDate).utc()
  return m1.diff(m2, precision)
}

function dateIsAfter (date, dateToCompare, precision) {
  const momentDate = moment(date).tz(store.getters.getFactoryTimezone)
  const momentDateToCompare = moment(dateToCompare).tz(store.getters.getFactoryTimezone)
  return momentDate.isAfter(momentDateToCompare, precision)
}

function dateIsAfterNow (date, precision) {
  const dateToCompare = getUTCNow()
  return dateIsAfter(date, dateToCompare, precision)
}

function dateIsBefore (date, dateToCompare, precision) {
  const momentDate = moment(date).tz(store.getters.getFactoryTimezone)
  const momentDateToCompare = moment(dateToCompare).tz(store.getters.getFactoryTimezone)
  return momentDate.isBefore(momentDateToCompare, precision)
}

function dateSameOrAfter (date, dateToCompare, precision) {
  const momentDate = moment(date).tz(store.getters.getFactoryTimezone)
  const momentDateToCompare = moment(dateToCompare).tz(store.getters.getFactoryTimezone)
  return momentDate.isSameOrAfter(momentDateToCompare, precision)
}

function getFirstDayOfWeek () {
  return moment().creationData().locale.firstDayOfWeek()
}

export function getLocaleFormat () {
  return moment().creationData().locale.longDateFormat('L')
}

function adjustDate (m, { day, month, week, year, startOf, endOf } = {}) {
  if (!moment.isMoment(m)) {
    throw new Error(`${m} is not a moment instance.`)
  }
  if (year !== null && year !== undefined) { m.year(year) }
  if (month !== null && month !== undefined) { m.month(month) }
  if (week !== null && week !== undefined) { m.isoWeek(week) }
  if (day !== null && day !== undefined) { m.date(day) }
  if (startOf !== null && startOf !== undefined) { m.startOf(startOf) }
  if (endOf !== null && endOf !== undefined) { m.endOf(endOf) }
}

function adjustDateOffset (m, {
  hourOffset,
  dayOffset,
  weekOffset,
  monthOffset,
  yearOffset,
} = {}) {
  if (!moment.isMoment(m)) {
    throw new Error(`${m} is not a moment instance.`)
  }
  const duration = moment.duration()
  if (yearOffset) { duration.add(yearOffset, 'y') }
  if (monthOffset) { duration.add(monthOffset, 'M') }
  if (weekOffset) { duration.add(weekOffset, 'w') }
  if (dayOffset) { duration.add(dayOffset, 'd') }
  if (hourOffset) { duration.add(hourOffset, 'h') }
  m.add(duration)
}

function getMonthNames () {
  return moment.months()
}

function getWeekDayNames () {
  return moment.weekdays()
}

function parseDateString (dateString, format) {
  return moment(dateString, format)
}

function getFormattedDate (date, {
  day,
  month,
  week,
  year,
  hourOffset,
  dayOffset,
  monthOffset,
  yearOffset,
  format = DATE_FORMAT,
} = {}) {
  const m = date ? moment(date) : moment()
  adjustDate(m, { day, month, week, year })
  adjustDateOffset(m, { hourOffset, dayOffset, monthOffset, yearOffset })
  return m.format(format)
}

export function getPlannedYear (year) {
  let start = null
  let end = moment().year(year)
  const firstWeekOfYear = getWeekNumber(new Date(year, 0, 1))
  if (firstWeekOfYear > 1) {
    start = moment().isoWeekYear(year-1).isoWeekday(1).isoWeek(firstWeekOfYear)
  } else {
    start = moment().isoWeekYear(year).isoWeekday(1).isoWeek(firstWeekOfYear)
  }
  end = moment().isoWeekYear(year + 1).isoWeekday(1).isoWeek(1)
  return {
    start: start.tz(store.getters.getFactoryTimezone).startOf('day').utc().format(ISO_DATETIME_FORMAT),
    end: end.tz(store.getters.getFactoryTimezone).startOf('day').utc().format(ISO_DATETIME_FORMAT),
  }
}

function getFormattedPlanDates ({ week, month, year } = {}) {
  const JANUARY = 0
  const DECEMBER = 11
  if (!year) {
    throw new Error('A year must be specified to get a formatted plan date')
  }
  let start = moment().year(year)
  let end = moment().year(year)
  if (week !== null && week !== undefined) {
    start = getFactoryUTCDate(undefined, { year: year, week: week, startOf: 'isoWeek' })
    end = getFactoryUTCDate(undefined, { year: year, week: week, weekOffset: 1, startOf: 'isoWeek' })
    const weeksOnYear = getWeeksOfYear(year)
    const isoWeekMonday = 1
    if (week > weeksOnYear || (month === JANUARY && week === weeksOnYear)) {
      start = moment().isoWeekYear(year - 1).isoWeek(week).isoWeekday(isoWeekMonday).startOf('day').utc().format(ISO_DATETIME_FORMAT)
      end = moment().isoWeekYear(year - 1).isoWeek(week + 1).isoWeekday(isoWeekMonday).startOf('day').utc().format(ISO_DATETIME_FORMAT)
    } else if (week == 1 && month == DECEMBER) {
      start = moment().isoWeekYear(year + 1).isoWeek(1).isoWeekday(isoWeekMonday).startOf('day').utc().format(ISO_DATETIME_FORMAT)
      end = moment().isoWeekYear(year + 1).isoWeek(2).isoWeekday(isoWeekMonday).startOf('day').utc().format(ISO_DATETIME_FORMAT)
    }
  }
  else if (month !== null && month !== undefined) {
    start = getFactoryUTCDate(undefined, { year: year, month, startOf: 'month' })
    end = getFactoryUTCDate(undefined, { year: year, month, monthOffset: 1, startOf: 'month' })
  }
  return {
    start: start,
    end: end,
  }
}

function getFactoryDateFromUTC (date, {
  hourOffset,
  dayOffset,
  weekOffset,
  monthOffset,
  yearOffset,
  format = DATE_FORMAT,
} = {}) {
  if (date === null) { return null }
  const m = moment(date, 'YYYY-MM-DDTHH:mm:ssZ').tz(store.getters.getFactoryTimezone)
  adjustDateOffset(m, { hourOffset, dayOffset, weekOffset, monthOffset, yearOffset })
  return m.format(format)
}

function getFactoryUTCDate (date, {
  day,
  month,
  week,
  year,
  hourOffset,
  dayOffset,
  weekOffset,
  monthOffset,
  yearOffset,
  startOf,
  endOf,
  parseFormat,
  format = ISO_DATETIME_FORMAT,
} = {}) {
  if (date === null) { return null }
  const m = parseFormat ? moment.tz(date, parseFormat, store.getters.getFactoryTimezone) : moment.tz(date, store.getters.getFactoryTimezone)
  adjustDate(m, { day, month, week, year, startOf, endOf })
  adjustDateOffset(m, { hourOffset, dayOffset, weekOffset, monthOffset, yearOffset })
  return m.utc().format(format)
}

function getDeviceTimezone () {
  return moment.tz.guess()
}

function getTimezoneOffset (timezone) {
  return moment().tz(timezone).utcOffset()
}

function getFactoryCurrentTime ({ format = ISO_DATETIME_FORMAT } = {}) {
  return moment().tz(store.getters.getFactoryTimezone).format(format)
}

function getCurrentFactoryDate () {
  const factoryDateString = getFactoryCurrentTime()
  const momentFactoryDate = parseDateString(factoryDateString, ISO_DATETIME_FORMAT)
  return momentFactoryDate.toDate()
}

export function subtractPeriod (date, quantityToSubtract, unit) {
  const momentDate = moment(date)
  const newMomentDate = momentDate.subtract(quantityToSubtract, unit)

  return newMomentDate.toDate()
}

export default {
  dateDiff,
  dateIsAfter,
  dateIsAfterNow,
  dateIsBefore,
  dateSameOrAfter,
  getDayOfTheMonth,
  getDeviceTimezone,
  getTimezoneOffset,
  getFirstDayOfWeek,
  getFactoryCurrentTime,
  getCurrentFactoryDate,
  getFactoryDateFromUTC,
  getFactoryUTCDate,
  parseDateString,
  getFormattedDate,
  getFormattedPlanDates,
  getMonth,
  getMonthName,
  getMonthNames,
  getPreviousDay,
  getTodayUTCBegin,
  getTodayUTCEnd,
  getWeekDayNames,
  getYear,
  DATE_FORMAT,
  DATE_FORMAT_SHORT,
  DATETIME_FORMAT,
  DATETIME_FORMAT_SHORT,
  HOUR_DATETIME_FORMAT,
  ISO_DATE_FORMAT,
  ISO_DATETIME_FORMAT,
  ISO_DATETIME_FORMAT_MILISECONDS,
  MONTH_NUMBER_FORMAT,
  MONTH_STRING_SHORT_FORMAT,
  NATURAL_DATE_FORMAT_SHORT,
  NATURAL_DATETIME_FORMAT,
  NATURAL_DATE_SHORT_WITH_HOUR,
  NATURAL_NAME_DATETIME_FORMAT,
  NATURAL_SHORT_WITH_HOUR,
  UNAWARE_DATETIME_FORMAT,
}
